'use client';

import React, { ReactNode } from 'react';
import { ErrorBoundary } from '@/shared/components/error/ErrorBoundary';
import { SecureAuthProvider } from '../auth/providers/SecureAuthProvider';
import { GlobalLoadingProvider } from './GlobalLoadingProvider';
import { ClientSentryProvider } from './ClientSentryProvider';
import { ReduxProvider } from './ReduxProvider';

interface AppProviderProps {
  children: ReactNode;
}

/**
 * AppProvider - Core infrastructure providers
 * 
 * Combines essential providers that are always needed:
 * - Error handling (ErrorBoundary, Sentry)
 * - Authentication
 * - State management (Redux)
 * - Global loading states
 */
// Detect public share routes that use HMAC token auth instead of session auth.
// These routes should not run the full auth stack to avoid interfering with
// the user's existing session in other tabs.
const isPublicShareRoute = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).has('token')
  && (window.location.pathname.startsWith('/chat/') || window.location.pathname.startsWith('/chat-launcher/'));

export function AppProvider({ children }: AppProviderProps) {
  return (
    <ErrorBoundary>
      <SecureAuthProvider lightweight={isPublicShareRoute}>
        <GlobalLoadingProvider>
          <ClientSentryProvider>
            <ReduxProvider>
              {children}
            </ReduxProvider>
          </ClientSentryProvider>
        </GlobalLoadingProvider>
      </SecureAuthProvider>
    </ErrorBoundary>
  );
}